import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { User, LogOut, Menu, X, Settings } from "lucide-react"; // Import Settings icon
import UserProfileModal from "./UserProfileModal"; // Import the new modal
import {
  selectHeaderNavMenuOpen,
  selectHeaderProfileMenuOpen,
  selectHeaderProfileModalOpen,
  setNavMenuOpen,
  setProfileMenuOpen,
  setProfileModalOpen,
} from "../store/headerSlice.js";

const Header = ({
  user,
  onLogout,
  api, // Receive api prop
  onUpdateUser, // Receive onUpdateUser prop
  navItems = [],
  currentViewMode,
  onViewModeChange,
  overdueTaskCount = 0,
}) => {
  const dispatch = useDispatch();
  const isProfileMenuOpen = useSelector(selectHeaderProfileMenuOpen);
  const isProfileModalOpen = useSelector(selectHeaderProfileModalOpen);
  const isNavMenuOpen = useSelector(selectHeaderNavMenuOpen);
  const underlineRef = useRef(null);
  const navContainerRef = useRef(null);

  // --- REFS FOR CLICK-OUTSIDE ---
  // We need refs for both desktop and mobile buttons/menus
  const profileBtnRef = useRef(null);
  const profileMenuRef = useRef(null);
  const mobileProfileBtnRef = useRef(null);
  const mobileProfileMenuRef = useRef(null);
  // --- END REFS ---

  useEffect(() => {
    if (!navItems || navItems.length === 0) return;

    const activeIndex = navItems.findIndex(
      (item) => item.mode === currentViewMode
    );

    if (activeIndex === -1) return;

    const navButtons = navContainerRef.current?.querySelectorAll("button");

    if (underlineRef.current && navButtons && navButtons[activeIndex]) {
      const activeBtn = navButtons[activeIndex];
      const containerRect = navContainerRef.current.getBoundingClientRect();
      const btnRect = activeBtn.getBoundingClientRect();

      const leftOffset = btnRect.left - containerRect.left + 4;
      const width = btnRect.width - 8;

      underlineRef.current.style.width = `${width}px`;
      underlineRef.current.style.left = `${leftOffset}px`;
    }
  }, [currentViewMode, navItems]);

  // --- NEW EFFECT FOR CLICK-OUTSIDE ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the profile menu is open
      if (isProfileMenuOpen) {
        // Check if the click was on the desktop button or menu
        const clickedDesktopBtn = profileBtnRef.current?.contains(event.target);
        const clickedDesktopMenu = profileMenuRef.current?.contains(event.target);
        
        // Check if the click was on the mobile button or menu
        const clickedMobileBtn = mobileProfileBtnRef.current?.contains(event.target);
        const clickedMobileMenu = mobileProfileMenuRef.current?.contains(event.target);

        // If the click was NOT on any of these elements, close the menu
        if (
          !clickedDesktopBtn &&
          !clickedDesktopMenu &&
          !clickedMobileBtn &&
          !clickedMobileMenu
        ) {
          dispatch(setProfileMenuOpen(false));
        }
      }
    };

    // Add listener only when the menu is open
    if (isProfileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup the listener when the component unmounts or the menu closes
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dispatch, isProfileMenuOpen]); // This effect depends on isProfileMenuOpen
  // --- END NEW EFFECT ---


  const handleOpenProfile = () => {
    dispatch(setProfileModalOpen(true));
    dispatch(setProfileMenuOpen(false));
  };

  return (
    <>
      <div className="bg-black text-white shadow-md relative">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center py-3 md:py-4">
            <div className="flex items-center flex-shrink-0">
              <img
                src="assets/logobar1.png"
                alt="Logo"
                className="h-10 sm:h-12 w-auto object-contain"
                onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/150x50/000000/FFFFFF?text=Logo'; }}
              />
            </div>

            <div className="hidden md:flex md:items-center md:space-x-6 relative ml-auto">
              <nav
                ref={navContainerRef}
                className="flex space-x-1 relative pb-2"
              >
                {navItems &&
                  navItems.map((item) => (
                    <button
                      key={item.mode}
                      onClick={() => onViewModeChange(item.mode)}
                      className={`relative flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200
                    ${
                      currentViewMode === item.mode
                        ? "text-white"
                        : "text-gray-300 hover:text-white hover:bg-gray-800"
                    }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                      {/* {item.mode === "notifications" && overdueTaskCount > 0 && ( */}
                      {(item.mode === "notifications" || item.mode === "overdue") && overdueTaskCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-white text-xs items-center justify-center font-bold">
                            {overdueTaskCount}
                          </span>
                        </span>
                      )}
                    </button>
                  ))}

                <div
                  ref={underlineRef}
                  className="absolute bottom-[-16px] h-[4px] bg-[#FF751F] rounded-full transition-all duration-300 ease-in-out"
                  style={{ width: 0, left: 0 }}
                ></div>
              </nav>

              <div className="relative">
                <button
                  ref={profileBtnRef} // <-- ADD REF
                  onClick={() => dispatch(setProfileMenuOpen(!isProfileMenuOpen))}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-800 hover:bg-gray-700 transition focus:outline-none"
                >
                  <User className="w-5 h-5 text-gray-300" />
                </button>

                {isProfileMenuOpen && (
                  <div 
                    ref={profileMenuRef} // <-- ADD REF
                    className="absolute right-0 mt-2 w-56 bg-gray-900 border border-gray-700 rounded-lg shadow-lg p-4 space-y-2 z-50"
                  >
                    <p className="text-lg text-gray-200">
                      Welcome, {user?.fullName || user?.username || "Guest"}
                    </p>
                    <p className="text-sm text-gray-400">
                      Role: {user?.role || "Not available"}
                    </p>
                    <div className="border-t border-gray-700 my-2"></div>
                    {/* --- NEW PROFILE BUTTON --- */}
                    <button
                      onClick={handleOpenProfile}
                      className="flex items-center w-full text-left text-gray-200 hover:bg-gray-800 text-sm py-2 px-3 rounded-md transition"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      My Profile
                    </button>
                    <button
                      onClick={onLogout}
                      className="flex items-center w-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-3 rounded-md transition"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center md:hidden">
              <div className="relative mr-3">
                <button
                  ref={mobileProfileBtnRef} // <-- ADD REF
                  onClick={() => dispatch(setProfileMenuOpen(!isProfileMenuOpen))}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-800 hover:bg-gray-700 transition focus:outline-none"
                >
                  <User className="w-5 h-5 text-gray-300" />
                </button>

                {isProfileMenuOpen && (
                  <div 
                    ref={mobileProfileMenuRef} // <-- ADD REF
                    className="absolute right-0 mt-2 w-56 bg-gray-900 border border-gray-700 rounded-lg shadow-lg p-4 space-y-2 z-50"
                  >
                    <p className="text-lg text-gray-200">
                      Welcome, {user?.fullName || user?.username || "Guest"}
                    </p>
                    <p className="text-sm text-gray-400">
                      Role: {user?.role || "Not available"}
                    </p>
                    <div className="border-t border-gray-700 my-2"></div>
                     {/* --- NEW PROFILE BUTTON (MOBILE) --- */}
                    <button
                      onClick={handleOpenProfile}
                      className="flex items-center w-full text-left text-gray-200 hover:bg-gray-800 text-sm py-2 px-3 rounded-md transition"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      My Profile
                    </button>
                    <button
                      onClick={onLogout}
                      className="flex items-center justify-center w-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-3 rounded-md transition mt-2"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>

              <button
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => dispatch(setNavMenuOpen(!isNavMenuOpen))}
              >
                {isNavMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {isNavMenuOpen && (
          <div className="md:hidden border-t border-gray-700">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navItems &&
                navItems.map((item) => (
                  <button
                    key={item.mode}
                    onClick={() => {
                      onViewModeChange(item.mode);
                      dispatch(setNavMenuOpen(false));
                    }}
                    className={`relative flex items-center gap-2 w-full text-left px-3 py-3 rounded-md text-base font-medium transition-all duration-200
                  ${
                    currentViewMode === item.mode
                      ? "text-white"
                      : "text-gray-300 hover:bg-gray-700 hover:text-white"
                  }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                    {/* {item.mode === "notifications" && overdueTaskCount > 0 && ( */}
                    {(item.mode === "notifications" || item.mode === "overdue") && overdueTaskCount > 0 &&(

                      <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
                        {overdueTaskCount}
                      </span>
                    )}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* --- RENDER THE MODAL --- */}
      {isProfileModalOpen && (
        <UserProfileModal
          user={user}
          api={api}
          onClose={() => dispatch(setProfileModalOpen(false))}
          onUpdateSuccess={onUpdateUser} // Pass the handler
        />
      )}
    </>
  );
};

export default Header;
